import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, TextField, FormControlLabel, Switch, IconButton, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import useAccountConfig from '../../hooks/useAccountConfig'
import { RequestService } from '../../services/RequestService'
import { useSnackbar } from '../../hooks'
import { CaffeineConfigurationForm } from '../../components'
import { useTranslation } from "react-i18next"
import { useConfigContextInfo } from '../../contexts/ConfigContext'
import { PLANS } from '../../utils/planNames'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f1f3f5;
  padding: 32px 48px;
`

const TopBoxes = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 32px;
`

const BoxItem = styled.div`
  flex: 1;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  font-weight: bold;

  p {
    margin-top: 8px;
    font-size: 22px;
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid #d6d7db;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  margin: 24px 0 0 0;
  gap: 8px;

  & > .MuiTextField-root {
    margin-bottom: 16px;
  }
`

const InlineField = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const AccountConfig = () => {
  const { loadingData: loading, data: accountConfigData } = useAccountConfig()
  const { successSnackbar, errorSnackbar } = useSnackbar()
  const [showConfig, setShowConfig] = useState(false)
  const [showCaffeineConfig, setShowCaffeineConfig] = useState(false)
  const [caffeineRows, setCaffeineRows] = useState([])
  const { t } = useTranslation('common')
  const formatStatus = status => status
    ? t(`account_config.status.${status}`, { defaultValue: t('no_data') })
    : t('loading')
  const context = useConfigContextInfo()

  const [accountConfigFormData, setAccountConfigFormData] = useState({
    alertness_benchmark: '',
    caffeine_limit: '',
    bac_8: '',
    bac_6: '',
    pvt_message: '',
    intake_message: '',
    config_message: '',
    schedule_message: '',
  })

  useEffect(() => {
    if (accountConfigData?.config) {
      setAccountConfigFormData(prevData => ({
        ...prevData,
        alertness_benchmark: accountConfigData.config.alertness_benchmark ?? '',
        caffeine_limit: accountConfigData.config.caffeine_limit ?? '',
        bac_8: accountConfigData.config.bac_8 ?? '',
        bac_6: accountConfigData.config.bac_6 ?? '',
        pvt_message: accountConfigData.config.pvt_message ?? '',
        intake_message: accountConfigData.config.intake_message ?? '',
        config_message: accountConfigData.config.config_message ?? '',
        schedule_message: accountConfigData.config.schedule_message ?? '',

      }))
    }
    if (accountConfigData?.caffeineProducts) {
      const initialCaffeineRows = accountConfigData.caffeineProducts.map(item => ({
        id: item.id,
        name: item.name || '',
        caffeine_mg: item.caffeine_mg ?? '',
        description: item.description || '',
        enabled: item.enabled || false,
        product_image_tag: item.product_image_tag || '',
      }));

      setCaffeineRows(initialCaffeineRows);
    }
  }, [accountConfigData])

  const handleInputChange = event => {
    setAccountConfigFormData(prevData => {
      const { name, value, type, checked } = event.target
      const newValue = type === 'checkbox' ? checked : value

      return { ...prevData, [name]: newValue }
    })
  }

  const handleSubmitAccountConfig = async event => {
    event.preventDefault()
    try {
      const response = await RequestService.updateAccountConfigurations({
        formData: accountConfigFormData,
      })
      if (response.status == 'success') {
        successSnackbar({ text: t('generic.success.update') })
      } else {
        errorSnackbar({ text: t('generic.error.update') })
      }
    } catch (e) {
      errorSnackbar({ text: t('generic.error.update') })
    }
  }

  const handleSubmitCaffeineConfig = async caffeineRows => {
    try {
      const { status, data } = await RequestService.updateAccountCaffeineConfig({
        caffeineRows,
      })
      if (status === 'success') {
        setCaffeineRows(data.caffeine_products)
        successSnackbar({ text: t('generic.success.update') })
      } else {
        errorSnackbar({ text: t('generic.error.update') })
      }
    } catch (e) {
      errorSnackbar({ text: t('generic.error.update') })
    }
  }

  return (
    <Container>
      <TopBoxes>
        <BoxItem>
          <h3>{t('account_config.total_users')}</h3>
          <p>
            {`${accountConfigData?.userCount} / ${accountConfigData?.usersLimit}` ||
              t('loading')}
          </p>
        </BoxItem>
        <BoxItem>
          <h3>{t('account_config.account_status')}</h3>
          <p>{formatStatus(accountConfigData?.accountStatus)}</p>
        </BoxItem>
        <BoxItem>
          <h3>{t('account_config.payment_status')}</h3>
          <p>{formatStatus(accountConfigData?.paymentStatus)}</p>
        </BoxItem>
      </TopBoxes>

      <div
        style={{
          display: 'flex',
          gap: '24px',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {/* Account Configurations */}
        <div style={{ width: '100%', boxSizing: 'border-box', marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2>{t('account_config.account_settings')}</h2>
            <IconButton onClick={() => setShowConfig(!showConfig)}>
              {showConfig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          <Collapse in={showConfig}>
            <FormContainer as="form" onSubmit={handleSubmitAccountConfig}>
              <TextField
                label={t('account_config.pvt_message')}
                fullWidth
                name="pvt_message"
                value={accountConfigFormData.pvt_message}
                onChange={handleInputChange}
              />
              <TextField
                label={t('account_config.intake_message')}
                fullWidth
                name="intake_message"
                value={accountConfigFormData.intake_message}
                onChange={handleInputChange}
              />
              <TextField
                label={t('account_config.onboarding_message')}
                fullWidth
                name="config_message"
                value={accountConfigFormData.config_message}
                onChange={handleInputChange}
              />
              <TextField
                label={t('account_config.shift_message')}
                fullWidth
                name="schedule_message"
                value={accountConfigFormData.schedule_message}
                onChange={handleInputChange}
              />
              <InlineField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={accountConfigFormData.bac_8_active}
                      onChange={handleInputChange}
                      name="bac_8_active"
                    />
                  }
                  label={t('account_config.bac_8_active')}
                />
              </InlineField>
              <InlineField>
                <FormControlLabel
                  control={
                    <Switch
                      checked={accountConfigFormData.bac_6_active}
                      onChange={handleInputChange}
                      name="bac_6_active"
                    />
                  }
                  label={t('account_config.bac_6_active')}
                />
              </InlineField>
              <TextField
                label={t('account_config.caffeine_limit')}
                type="number"
                fullWidth
                name="caffeine_limit"
                value={accountConfigFormData.caffeine_limit}
                onChange={handleInputChange}
              />
              {(context?.plan === PLANS.OPTIMIZED ||
              context?.plan === PLANS.PREDICTION) && (
                <TextField
                  label={t('account_config.alertness_benchmark')}
                  type="number"
                  fullWidth
                  name="alertness_benchmark"
                  value={accountConfigFormData.alertness_benchmark}
                  onChange={handleInputChange}
                />
              )}
              <Button variant="contained" color="primary" type="submit">
                {t('save', 'Save')}
              </Button>
            </FormContainer>
          </Collapse>
        </div>

        {/* Caffeine Configurations */}
        <div style={{ width: '100%', boxSizing: 'border-box', marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <h2>{t('account_config.caffeine_settings')}</h2>
            <IconButton onClick={() => setShowCaffeineConfig(!showCaffeineConfig)}>
              {showCaffeineConfig ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          <Collapse in={showCaffeineConfig}>
            <CaffeineConfigurationForm
              initialConfigurations={caffeineRows}
              onSubmit={handleSubmitCaffeineConfig}
              canEditExisting={context?.role === 'system_admin'}
              products={accountConfigData?.availableCaffeineProducts || []}
            />
          </Collapse>
        </div>
      </div>
    </Container>
  )

}

export default AccountConfig
